import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { Resource } from '@opentelemetry/resources';
import { SemanticResourceAttributes } from '@opentelemetry/semantic-conventions';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { API_CONFIG } from '../services/staticDataConfig';


// Normaliza paths: substitui IDs e UUIDs para evitar explosão de cardinalidade
// Ex: /api/pacientes/123 → /api/pacientes/:id
function normalizePath(pathname: string): string {
  return pathname
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ':uuid')
    .replace(/\/\d{2,}/g, '/:id');
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function getTracePropagationTargets() {
  const origins = new Set<string>();

  try {
    origins.add(window.location.origin);
    origins.add(new URL(API_CONFIG.BASE_URL).origin);
  } catch {
    // Em caso de URL inválida, manter apenas origem atual.
  }

  return Array.from(origins).map((origin) => new RegExp(`^${escapeRegExp(origin)}/`));
}

function getTracingIgnoredUrls() {
  return [
    /^https:\/\/sdk\.mercadopago\.com\//,
    /^https:\/\/api\.mercadolibre\.com\//,
    /^https:\/\/http2\.mlstatic\.com\//,
    /^https:\/\/secure-fields\.mercadopago\.com\//,
  ];
}

export function initializeFrontendObservability() {
  const serviceName = 'sophya-frontend';
  
  // Sempre enviar traces pelo mesmo dominio da aplicacao.
  // O Nginx faz proxy de /otel/* para o coletor Alloy interno.
  const endpoint = `${window.location.origin}/otel/v1/traces`;
  const traceTargets = getTracePropagationTargets();
  const ignoredUrls = getTracingIgnoredUrls();

  const provider = new WebTracerProvider({
    resource: new Resource({
      [SemanticResourceAttributes.SERVICE_NAME]: serviceName,
      [SemanticResourceAttributes.DEPLOYMENT_ENVIRONMENT]: 'production',
    }),
  });

  const exporter = new OTLPTraceExporter({
    url: endpoint,
  });

  provider.addSpanProcessor(new BatchSpanProcessor(exporter));

  provider.register({
    contextManager: new ZoneContextManager(),
  });

  registerInstrumentations({
    instrumentations: [
      new XMLHttpRequestInstrumentation({
        propagateTraceHeaderCorsUrls: traceTargets,
        ignoreUrls: ignoredUrls,
        applyCustomAttributesOnSpan: (span, xhr) => {
          try {
            const url = new URL((xhr as any).responseURL || '', window.location.origin);
            // Ler método do atributo já setado pela instrumentação automática
            const method = ((span as any).attributes?.['http.method'] as string)
              || (xhr as any)._method || 'GET';
            const path = normalizePath(url.pathname);
            span.setAttribute('http.route', `${method} ${path}`);
            span.setAttribute('http.request.method', method);
            // peer.service → cria nós por rota no service graph (mesmo mecanismo do MySQL)
            if (url.pathname.startsWith('/api/')) {
              span.setAttribute('peer.service', `backend:${method} ${path}`);
            }
          } catch (_) { /* ignore */ }
        },
      }),
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls: traceTargets,
        ignoreUrls: ignoredUrls,
        applyCustomAttributesOnSpan: (span, request) => {
          try {
            const req = request as Request;
            const url = new URL(req.url, window.location.origin);
            const method = req.method || 'GET';
            const path = normalizePath(url.pathname);
            span.setAttribute('http.route', `${method} ${path}`);
            span.setAttribute('http.request.method', method);
            // peer.service → cria nós por rota no service graph (mesmo mecanismo do MySQL)
            if (url.pathname.startsWith('/api/')) {
              span.setAttribute('peer.service', `backend:${method} ${path}`);
            }
          } catch (_) { /* ignore */ }
        },
      }),
    ],
  });

  console.log(`[OTEL-WEB] Inicializado para ${serviceName}. Endpoint: ${endpoint}`);
}
